import { Checkbox, Radio } from "../../../utils/local-antd";
import { ControllerFieldState, ControllerRenderProps } from "react-hook-form";
import styled from "styled-components";

import { AtiraSelect } from "../../../components/AtiraSelect";
import { Flex } from "../../../components/Flex";
import { Input } from "../../../components/Input";
import { Text } from "../../../components/Text";
import { Textarea } from "../../../components/Textarea";
import { FormInput } from "../../../model/form/FormInput";
import { InputTypes } from "../../../model/form/types/InputTypes.enum";
import { Lengths } from "../../../model/shared/enum/Lengths.enum";
import { TestIDs } from "../../../tests/types/testIds";
import { isBoolean } from "lodash";
import { ModuleInput } from "../../../model/module/types/module-definition/ModuleInput";
import { AtiraDatePickerFlat } from "../../../components/AtiraDatePickerFlat";
import { isCustomDateInput } from "../../../utils/isCustomDateInput";

const RequiredStar = styled.span`
  color: ${({ theme }) => theme.danger};
`;

const StyledRadioGroup = styled(Radio.Group)`
  label:last-child .ant-wave-target + span {
    padding-inline-end: 0;
  }
`;

/**
 * RegularInputComponent's numeric paste handler calls onChange with the
 * pasted string directly, bypassing the normal ChangeEvent - so this can't
 * always assume `e.currentTarget.value` exists.
 */
const extractInputValue = (
  e: React.ChangeEvent<{ value: string }> | string,
) => (typeof e === "string" ? e : e.currentTarget.value);

export const getRenderedFieldByType = (
  input: FormInput | Partial<FormInput> | ModuleInput,
  field: ControllerRenderProps<any, any>,
  fieldState: ControllerFieldState,
  fieldLabel?: string,
  fieldRequired?: boolean,
  config: Record<string, any> = {},
) => {
  const type = input.type?.toLowerCase() as InputTypes;
  const { value, onChange } = field;
  const error = fieldState.error;
  const isRequired = isBoolean(fieldRequired)
    ? fieldRequired
    : input.settings?.required === true;
  const isNumberInput = type === InputTypes.NUMBER?.toLowerCase();
  const maxLength = isNumberInput
    ? Math.min(input.settings?.maxChar || Lengths.NUMBER, Lengths.NUMBER)
    : input.settings?.maxChar;

  switch (type) {
    case InputTypes.SELECT:
      return (
        <AtiraSelect
          required={isRequired}
          title={fieldLabel || input.label}
          options={input.options?.map((option) => ({
            value: option,
            label: option,
          }))}
          onChange={onChange}
          value={value}
          valid={!error}
          errorMessage={error?.message}
          style={{ fontSize: "1.2rem", width: "100%" }}
          className={TestIDs.entries.createEdit.form.fields.field(input.name!)}
        />
      );
    case InputTypes.RADIO:
      if (value === undefined && input.options?.length) {
        onChange(input.options[0]);
      }
      return (
        <Flex flexDirection="column" gap="s" alignItems="flex-start">
          <Text>
            {fieldLabel || input.label}
            {isRequired ? <RequiredStar> *</RequiredStar> : null}
          </Text>

          <StyledRadioGroup value={value}>
            {input.options?.map((option, index) => (
              <Radio
                key={`${option}-${index}`}
                value={option}
                onChange={onChange}
                checked={index === 0}
                className={TestIDs.entries.createEdit.form.fields.field(
                  input.name!,
                )}
              >
                {option}
              </Radio>
            ))}
          </StyledRadioGroup>
          {error?.message ? (
            <Text color="danger" fontSize="s">
              {error.message}
            </Text>
          ) : null}
        </Flex>
      );
    case InputTypes.CHECKBOX:
      return (
        <Flex flexDirection="column" gap="xs" alignItems="flex-start">
          <Checkbox
            checked={String(value) === "true"}
            onChange={(e) => onChange(e.target.checked)}
            className={TestIDs.entries.createEdit.form.fields.field(
              input.name!,
            )}
            style={{ alignItems: "center" }}
          >
            {fieldLabel || input.label}
            {isRequired ? <RequiredStar>{" *"}</RequiredStar> : null}
          </Checkbox>
          {error?.message ? (
            <Text color="danger" fontSize="s">
              {error.message}
            </Text>
          ) : null}
        </Flex>
      );
    case InputTypes.TEXTAREA:
      return (
        <Textarea
          title={fieldLabel || input.label}
          name={input.name}
          value={type === InputTypes.DATE.toLowerCase() ? value : value || ""}
          onChange={(e) => {
            const raw = extractInputValue(e);
            onChange(
              type === InputTypes.NUMBER?.toLowerCase()
                ? raw.replace(/[^\d+]/g, "")
                : raw,
            );
          }}
          valid={!Boolean(error?.message?.length)}
          errorMessage={error?.message || ""}
          style={{ fontSize: "1.2rem" }}
          required={isRequired}
          maxLength={input.settings?.maxChar}
          dataTestId={TestIDs.entries.createEdit.form.fields.field(input.name!)}
        />
      );

    default:
      if (isCustomDateInput(input.type, input.dateInputConfig)) {
        return (
          <AtiraDatePickerFlat
            onChange={onChange}
            name={input.name}
            title={fieldLabel || input.label}
            valid={!error}
            value={value}
            placeholder={fieldLabel || input.label}
            errorMessage={error?.message}
            required={isRequired}
            config={input.dateInputConfig}
            possibleBookedSlots={config.possibleBookedSlots}
            dataTestId={TestIDs.entries.createEdit.form.fields.field(
              input.name!,
            )}
          />
        );
      }

      return (
        <Input
          type={type?.toLowerCase()}
          title={fieldLabel || input.label}
          name={input.name}
          // `?? ""` (not `||`) - a legitimate `0` is falsy and `||` would
          // blank it back out on the very next render.
          value={value ?? ""}
          onChangeDate={onChange}
          onChange={(e) => {
            const raw = extractInputValue(e);

            if (isNumberInput) {
              const digits = raw.replace(/\D/g, "");
              // Number("") is 0, not NaN - coercing an emptied field to 0
              // makes an optional numeric field impossible to clear (and
              // then, together with `value ?? ""` above, indistinguishable
              // from an intentional 0).
              onChange(digits === "" ? "" : Number(digits));
              return;
            }

            onChange(raw);
          }}
          padding="0 0.5rem"
          valid={!error}
          errorMessage={error?.message}
          style={{ fontSize: "1.2rem" }}
          required={isRequired}
          maxLength={maxLength}
          dataTestId={TestIDs.entries.createEdit.form.fields.field(input.name!)}
        />
      );
  }
};
